import { loadExcelJS } from '@ubeya/shared/utils/spreadsheetLibraries';
import { saveAs } from 'file-saver';
import * as Sentry from '@sentry/react';
import { removeSpecialCharsOfData } from '@ubeya/shared/utils/securityCheck';

const DAY_COL_WIDTH = 8;
const CONTENT_START_ROW = 24;
const MIN_NOTES_COL_WIDTH = 40;

const OB_LOGO_START_COL = 4;
const OB_LOGO_END_COL = 8;
const OB_LOGO_START_ROW = 0;
const OB_LOGO_END_ROW = 7;

const ONB_WEEKDAY_KEYS = new Set(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);

const applyObColumnWidths = ({ worksheet, headerKeys, lastRowWithData }) => {
  headerKeys.forEach((key, index) => {
    const col = index + 1;

    if (ONB_WEEKDAY_KEYS.has(key)) {
      worksheet.getColumn(col).width = DAY_COL_WIDTH;
      return;
    }

    let maxLength = key.length;
    for (let row = CONTENT_START_ROW; row <= lastRowWithData; row++) {
      const length = String(worksheet.getCell(row, col).value ?? '').length;
      if (length > maxLength) maxLength = length;
    }
    const minWidth = key === 'Notes' ? MIN_NOTES_COL_WIDTH : 0;
    // +2 for extra padding
    worksheet.getColumn(col).width = Math.max(maxLength + 2, minWidth);
  });
};

const getImageExtension = (url) => {
  const pathname = new URL(url).pathname;
  const ext = pathname.split('.').pop().toLowerCase();
  if (['png', 'jpeg', 'jpg', 'gif'].includes(ext)) return ext;
  return 'png';
};

const addObLogoToWorksheet = async ({ workbook, worksheet, logo }) => {
  if (!logo) return;

  try {
    const response = await fetch(logo, { mode: 'cors', credentials: 'omit', cache: 'reload' });
    if (!response.ok) return;

    const buffer = await response.arrayBuffer();
    const extension = getImageExtension(logo);
    const imageId = workbook.addImage({ buffer, extension });

    worksheet.addImage(imageId, {
      tl: { col: OB_LOGO_START_COL, row: OB_LOGO_START_ROW },
      br: { col: OB_LOGO_END_COL, row: OB_LOGO_END_ROW }
    });
  } catch (error) {
    // Logo is optional; export should still succeed without it.
    Sentry.captureException(error, { extra: { logo, context: 'O&B export logo load failed' } });
  }
};

export const clientInvoicesExcel = async ({
  name,
  firstReport: { data: baseFirstReportData },
  secondReport: { data: baseSecondReportData }
}) => {
  const Excel = await loadExcelJS();
  const workbook = new Excel.Workbook();
  const workSheetName = (name || '').replace(/[*?:/\\[\]]/g, '');

  const firstWorksheet = workbook.addWorksheet(workSheetName, { title: 'tet' });
  const secondWorkSheet = workbook.addWorksheet(`2. ${workSheetName} - full`, { title: 'tet' });

  /* Security check */
  const firstReportData = removeSpecialCharsOfData(baseFirstReportData);
  const secondReportData = removeSpecialCharsOfData(baseSecondReportData);

  firstWorksheet.columns = Object.keys(firstReportData?.[0] || {}).map((column) => ({
    key: column
  }));

  const firstWorksheetRows = firstReportData.map((item) => Object.values(item));

  firstWorksheet.insertRows(1, firstWorksheetRows);

  secondWorkSheet.getRow(1).values = Object.keys(secondReportData?.[0] || {});
  secondWorkSheet.columns = Object.keys(secondReportData?.[0] || {}).map((column) => ({
    key: column
  }));
  secondWorkSheet.insertRows(
    2,
    secondReportData.map((item) => Object.values(item))
  );

  workbook.xlsx.writeBuffer().then((data) => {
    const blob = new Blob([data], { type: 'application/octet-stream' });
    saveAs(blob, `${workSheetName}.xlsx`);
  });
};

export const exportExcelOB = async ({
  baseData = [],
  name,
  datesArray,
  generalInfo,
  summary,
  isEmptyExport = false,
  logo
}) => {
  const workSheetName = (name || '').replace(/[*?:/\\[\]]/g, '');

  const Excel = await loadExcelJS();
  const workbook = new Excel.Workbook();
  const worksheet = workbook.addWorksheet(workSheetName, { title: 'tet' });

  /* Security check */
  const securedData = removeSpecialCharsOfData(baseData);

  let i = 0;
  const data = securedData.reduce((output, current) => {
    let selectedIndex = i + 1;
    if (current.connected) {
      selectedIndex = '';
    } else {
      i = i + 1;
    }

    // eslint-disable-next-line unused-imports/no-unused-vars
    const { connected, showRow, ...restCurrent } = current;

    if (!isEmptyExport && !showRow) {
      return output;
    }

    output.push({ selectedIndex, ...restCurrent });
    return output;
  }, []);

  worksheet.getCell('B10').value = 'Client:';
  worksheet.getCell('B10').font = { bold: true };
  worksheet.getCell('B11').value = 'Contract:';
  worksheet.getCell('B11').font = { bold: true };
  worksheet.getCell('B12').value = 'Week ending:';
  worksheet.getCell('B12').font = { bold: true };
  worksheet.getCell('B13').value = 'Event booker:';
  worksheet.getCell('B13').font = { bold: true };
  worksheet.getCell('B14').value = 'Site reference';
  worksheet.getCell('B14').font = { bold: true };
  worksheet.getCell('B15').value = 'FAO';
  worksheet.getCell('B15').font = { bold: true };

  worksheet.getCell('E18').value = 'TO FACILITATE PAYMENT OF WAGES';
  worksheet.getCell('E18').font = { bold: true };
  worksheet.getCell('E19').value = 'PLEASE RETURN SIGNED COPY TO YOUR ACCOUNT MANAGER BY 12PM ON MONDAY';
  worksheet.getCell('E19').font = { bold: true };

  const range1 = ['E18', 'F18', 'G18', 'H18', 'I18', 'J18', 'K18']
    .map((cell) => worksheet.getCell(cell).value || '')
    .join(' ');

  const range2 = ['E19', 'F19', 'G19', 'H19', 'I19', 'J19', 'K19']
    .map((cell) => worksheet.getCell(cell).value || '')
    .join(' ');

  worksheet.mergeCells('E18:K19');
  const mergedCell = worksheet.getCell('E18');
  mergedCell.value = `${range1}\n${range2}`; // Separate rows with a newline

  // Style the merged cell with a border
  mergedCell.border = {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' }
  };

  // Enable text wrapping and center alignment
  mergedCell.alignment = {
    wrapText: true,
    vertical: 'middle',
    horizontal: 'center'
  };

  // Ensure the merged box is tall enough so the wrapped text is not clipped.
  worksheet.getRow(18).height = 24;
  worksheet.getRow(19).height = 24;

  //value
  const { eventName, clientName, bookerName, clientContactName, lastSundayOfWeek, invoiceNumber } = generalInfo || {};

  worksheet.getCell('C10').value = clientName;
  worksheet.getCell('C10').font = { color: { argb: '0000FF' } };
  worksheet.getCell('C11').value = eventName;
  worksheet.getCell('C11').font = { color: { argb: '0000FF' } };
  worksheet.getCell('C12').value = lastSundayOfWeek;
  worksheet.getCell('C12').font = { color: { argb: '0000FF' } };
  worksheet.getCell('C13').value = bookerName;
  worksheet.getCell('C13').font = { color: { argb: '0000FF' } };
  worksheet.getCell('C14').value = invoiceNumber;
  worksheet.getCell('C14').font = { color: { argb: '0000FF' } };
  worksheet.getCell('C15').value = clientContactName;
  worksheet.getCell('C15').font = { color: { argb: '0000FF' } };

  //dates row
  worksheet.getRow(23).values = datesArray;
  worksheet.getRow(23).font = { bold: true };
  worksheet.getRow(23).alignment = { vertical: 'middle', horizontal: 'center' };

  const headerKeys = Object.keys(data[0]).filter((key) => !key.includes('dateOfDay'));

  // column row
  worksheet.getRow(24).values = headerKeys;
  worksheet.getRow(24).font = { bold: true };
  worksheet.getRow(24).alignment = { vertical: 'middle', horizontal: 'center' };
  worksheet.getCell('A24').value = '';
  worksheet.getColumn('A').alignment = { vertical: 'middle', horizontal: 'center' };

  //title
  worksheet.getCell('B22').value = name;
  worksheet.mergeCells('B22:K22');
  worksheet.getRow(22).alignment = { vertical: 'middle', horizontal: 'center' };
  worksheet.getRow(22).font = { bold: true };

  worksheet.columns = Object.keys(data[0]).map((column) => ({
    key: column
  }));

  const rows = data.map((item) => Object.values(item));
  worksheet.insertRows(CONTENT_START_ROW + 1, rows);

  const lastRowWithData = worksheet.rowCount;
  applyObColumnWidths({ worksheet, headerKeys, lastRowWithData });

  await addObLogoToWorksheet({ workbook, worksheet, logo });

  for (let row = CONTENT_START_ROW + 1; row <= lastRowWithData; row++) {
    worksheet.getRow(row).height = 22;
  }
  if (!isEmptyExport) {
    // Define the specific range to apply borders
    const startRow = 23;
    const endRow = worksheet.rowCount;
    const startCol = 2; // Column B
    const endCol = 15;

    // Apply borders to the specific range
    for (let row = startRow; row <= endRow; row++) {
      for (let col = startCol; col <= endCol; col++) {
        const cell = worksheet.getCell(row, col);
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      }
    }

    worksheet.getCell('E24').value = '';

    worksheet.getCell(`J${lastRowWithData + 2}`).value = 'VAT %';
    worksheet.getCell(`K${lastRowWithData + 2}`).value = 'Net EUR';
    worksheet.getCell(`L${lastRowWithData + 2}`).value = 'VAT EUR';
    worksheet.getCell(`M${lastRowWithData + 2}`).value = 'Total EUR';

    ['J', 'K', 'L', 'M'].forEach((col) => {
      worksheet.getCell(`${col}${lastRowWithData + 2}`).font = { bold: true };
      worksheet.getCell(`${col}${lastRowWithData + 2}`).alignment = { vertical: 'middle', horizontal: 'center' };

      const borderStyle = { style: 'thin', color: { argb: '000000' } };
      worksheet.getCell(`${col}${lastRowWithData + 3}`).border = {
        top: borderStyle,
        left: borderStyle,
        bottom: borderStyle,
        right: borderStyle
      };
    });

    ['J', 'K', 'L', 'M'].forEach((col) => {
      worksheet.getCell(`${col}${lastRowWithData + 2}`).alignment = { vertical: 'middle', horizontal: 'center' };
      const borderStyle = { style: 'thin', color: { argb: '000000' } };
      worksheet.getCell(`${col}${lastRowWithData + 2}`).border = {
        top: borderStyle,
        left: borderStyle,
        bottom: borderStyle,
        right: borderStyle
      };
    });

    worksheet.getCell(`J${lastRowWithData + 3}`).value = '0.00';
    worksheet.getCell(`K${lastRowWithData + 3}`).value = summary.total;
    worksheet.getCell(`L${lastRowWithData + 3}`).value = '0.00';
    worksheet.getCell(`M${lastRowWithData + 3}`).value = summary.total;
  } else {
    // Define the specific range to apply borders
    const startRow = 23;
    const endRow = worksheet.rowCount;
    const startCol = 2; // Column B
    const endCol = 13;

    // Apply borders to the specific range
    for (let row = startRow; row <= endRow; row++) {
      for (let col = startCol; col <= endCol; col++) {
        const cell = worksheet.getCell(row, col);
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      }
    }

    worksheet.getCell(`B${lastRowWithData + 5}`).value = 'Signature O&B';
    worksheet.getCell(`B${lastRowWithData + 5}`).font = { bold: true };
    worksheet.getCell(`B${lastRowWithData + 5}`).alignment = { vertical: 'middle', horizontal: 'left' };
    worksheet.getCell(`C${lastRowWithData + 5}`).border = { bottom: { style: 'thin', color: { argb: '000000' } } };
    worksheet.getCell(`D${lastRowWithData + 5}`).border = { bottom: { style: 'thin', color: { argb: '000000' } } };

    worksheet.getCell(`H${lastRowWithData + 5}`).value = 'Signature client';
    worksheet.getCell(`H${lastRowWithData + 5}`).font = { bold: true };
    worksheet.getCell(`H${lastRowWithData + 5}`).alignment = { vertical: 'middle', horizontal: 'left' };
    worksheet.getCell(`I${lastRowWithData + 5}`).border = { bottom: { style: 'thin', color: { argb: '000000' } } };
    worksheet.getCell(`J${lastRowWithData + 5}`).border = { bottom: { style: 'thin', color: { argb: '000000' } } };

    worksheet.getCell(`I${lastRowWithData + 2}`).value = 'All hours, trade codes and descriptions entered and signed';
    worksheet.getCell(`I${lastRowWithData + 3}`).value =
      'for on this timesheet are in accordance with our Terms of Business.';

    worksheet.getCell(`I${lastRowWithData + 7}`).value = clientName;
    worksheet.getCell(`I${lastRowWithData + 8}`).value = 'As correct in respect of actual hours worked';

    worksheet.getCell(`H${lastRowWithData + 10}`).value =
      'N.B. Please exclude lunch break - signed timesheet is conclusive of hours worked';
  }

  // Set page setup for printing
  worksheet.pageSetup.orientation = 'landscape'; // Use 'landscape' if needed
  worksheet.pageSetup.paperSize = 9; // Paper size (9 = A4)
  worksheet.pageSetup.fitToPage = true;
  worksheet.pageSetup.fitToWidth = 1;
  worksheet.pageSetup.fitToHeight = 1;

  const bufferData = await workbook.xlsx.writeBuffer();
  const blob = new Blob([bufferData], { type: 'application/octet-stream' });
  saveAs(blob, `${workSheetName}.xlsx`);
};

export const exportBCSExcel = async ({ name, headerRow, baseData, isEAI = false }) => {
  const workSheetName = (name || '').replace(/[*?:/\\[\]]/g, '');

  const Excel = await loadExcelJS();
  const workbook = new Excel.Workbook();
  const worksheet = workbook.addWorksheet(workSheetName, { title: 'tet' });

  /* Security check */
  const data = removeSpecialCharsOfData(baseData);

  worksheet.getRow(1).values = headerRow;
  worksheet.columns = Object.keys(data[0]);

  const rows = (data || data).map((item) => Object.values(item));
  worksheet.insertRows(2, rows);

  workbook.xlsx.writeBuffer().then((bufferData) => {
    const blob = new Blob([bufferData], { type: 'application/octet-stream' });
    saveAs(blob, `${workSheetName}.${isEAI ? 'eai' : 'csv'}`);
  });
};

export const exportBCSUpdateExcel = async ({ name, baseData, asCSV = false }) => {
  const workSheetName = (name || '').replace(/[*?:/\\[\]]/g, '');

  const Excel = await loadExcelJS();
  const workbook = new Excel.Workbook();
  const worksheet = workbook.addWorksheet(workSheetName, { title: 'tet' });

  /* Security check */
  const data = removeSpecialCharsOfData(baseData);

  const rows = (data || data).map((item) => Object.values(item));
  worksheet.insertRows(1, rows);

  workbook.xlsx.writeBuffer().then((bufferData) => {
    const blob = new Blob([bufferData], { type: 'application/octet-stream' });
    saveAs(blob, `${workSheetName}.${asCSV ? 'csv' : 'xlsx'}`);
  });
};

export const FILE_TYPE = {
  CSV: 'csv',
  XLSX: 'xlsx',
  EAI: 'eai',
  PDF: 'pdf'
};

/**
 * Save binary data as a file.
 * @param {ArrayBuffer | Buffer} data - The binary data to be saved.
 * @param {FILE_TYPE} type - The type or format of the file.
 *                         Use one of the values from the FILE_TYPE constant.
 */

// todo : add an option to send filename
export const saveFile = ({ data, type, fileName }) => {
  const dataBuffer = new Uint8Array(data);
  const blob = new Blob([dataBuffer], { type: 'application/octet-stream' });
  saveAs(blob, `${fileName}.${type}`);
};
